import { AgentThreadTaskStatus } from "@/api";
import { ORIGINAL_MUSIC_VOICE_ID } from "@/component/MediaEditorModal/component/VoiceSelector";
import { ArtifactsViewerData } from "@/hooks/useArtifactsViewerData";
import { TimelinePlaybackMode } from "@/store/helpers/threadSessionStorage";
import {
  GenerateImageForShotByIndexTask,
  GenerateReferenceImageByIndexTask,
  GenerateVideoForShotByIndexTask,
  RtcTask,
} from "@/store/thread";
import { unseenVersionStore } from "@/store/unseenVersion";
import {
  AvatarAudioType,
  MediaEditorItem,
  MediaVersion,
  MediaViewState,
} from "@/types/mediaArtifact";
import { mergeAndSortVersions } from "@/util/artifact";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import { applyResolvedFields } from "./mediaUtils";
import { ModelResolver } from "./modelResolver";

export interface BuildContext {
  rtcTasks: RtcTask[];
  playbackMode: TimelinePlaybackMode;
  resolveModel?: ModelResolver;
}

export function buildReferenceImageItems(
  data: ArtifactsViewerData["referenceImage"]["data"],
  context: BuildContext,
  viewState: MediaViewState = {
    manuallyAddedVersions: {},
    selectedVersions: {},
    metadataOverrides: {},
  }
): MediaEditorItem[] {
  const { rtcTasks, playbackMode, resolveModel } = context;
  const { manuallyAddedVersions, selectedVersions, metadataOverrides } =
    viewState;
  return data.images.map((image, index): MediaEditorItem => {
    const metadata = data.imagesMetadatas[index];
    const metadataVersions = metadata?.image.versions || [];
    const generatedVersions = metadataVersions.map((version): MediaVersion => {
      const v: MediaVersion = {
        id: version.identity || version.file_path,
        src: convertFilePathToUrl(version.file_path),
        previewSrc: convertFilePathToUrl(version.file_path),
        filePath: version.file_path,
        source: version.source,
        attachmentList: (version.model_config?.files || []).map((item) => ({
          id:
            item || `attachment-${Math.random().toString(36).substring(2, 15)}`,
          type: "url",
          url: convertFilePathToUrl(item),
        })),
        status: "success",
        model: resolveModel?.(version.model_name),
        dialogue: "",
        voiceId: "",
        prompt:
          version.related_data.prompt || version.related_data.caption || "",
        createdTime: new Date(version.create_time).getTime(),
      };
      return applyResolvedFields(v, "image", playbackMode);
    });

    // Detect newly generated versions and mark them as unseen
    generatedVersions.forEach((v) => {
      if (unseenVersionStore.isPendingTask(v.id)) {
        unseenVersionStore.addUnseenVersion(v.id);
        unseenVersionStore.removePendingTaskId(v.id);
      }
    });

    // Get generating versions from RTC tasks
    const taskVersions = rtcTasks
      .filter((task) => {
        return (
          task.data.tool_type === "generate_reference_image_by_index" &&
          task.data.call_args.index === index + 1 &&
          task.status !== AgentThreadTaskStatus.Success
        );
      })
      .map((task): MediaVersion => {
        const data = task.data as GenerateReferenceImageByIndexTask;
        return {
          id: task.id,
          src: "",
          previewSrc: "",
          filePath: "",
          source: "generate",
          prompt: data.init_args.user_prompt,
          status:
            task.status === AgentThreadTaskStatus.Failed
              ? "failed"
              : "generating",
          createdTime: new Date(task.create_time).getTime(),
          attachmentList: (data.init_args.user_specified_images || []).map(
            (item) => ({
              id:
                item ||
                `attachment-${Math.random().toString(36).substring(2, 15)}`,
              type: "url",
              url: convertFilePathToUrl(item),
            })
          ),
          model: resolveModel?.(data.init_args.i2i_model_name),
        };
      })
      .map((v) => applyResolvedFields(v, "image", playbackMode));

    const manualVersions = (manuallyAddedVersions[index] || []).map((v) =>
      applyResolvedFields({ ...v }, "image", playbackMode)
    );

    const versions = mergeAndSortVersions(
      generatedVersions,
      taskVersions,
      manualVersions
    );

    let current =
      versions.find((version) => version.id === selectedVersions[index]) ||
      generatedVersions.find((version) => version.filePath === image.file_path);

    if (current) {
      const overrides = metadataOverrides[index];
      if (overrides) {
        current = { ...current, ...overrides };
      }
      current = applyResolvedFields(current, "image", playbackMode);
    }

    if (!current) {
      return {
        mediaType: "image",
        current: {
          id: image.file_path || `ref-image-${index}`,
          src: "",
          previewSrc: "",
          filePath: image.file_path,
          source: "generate" as const,
          prompt: image.prompt,
          attachmentList: [],
          status: "generating" as const,
          createdTime: 0,
          dialogue: "",
          voiceId: "",
        },
        versions: [],
      };
    }

    return {
      current,
      versions,
      mediaType: "image",
    };
  });
}

export function buildImageStoryboardItems(
  data: ArtifactsViewerData["imageStoryboard"]["data"],
  context: BuildContext,
  viewState: MediaViewState = {
    manuallyAddedVersions: {},
    selectedVersions: {},
    metadataOverrides: {},
  }
): MediaEditorItem[] {
  const { rtcTasks, playbackMode, resolveModel } = context;
  const { manuallyAddedVersions, selectedVersions, metadataOverrides } =
    viewState;
  return data.shots.map((shot, index): MediaEditorItem => {
    const metadata = data.shotsMetadatas[index];
    const metadataVersions = metadata?.start_frame_image.versions || [];
    const generatedVersions = metadataVersions.map((version): MediaVersion => {
      const v: MediaVersion = {
        id: version.identity || version.file_path,
        src: convertFilePathToUrl(version.file_path),
        previewSrc: convertFilePathToUrl(version.file_path),
        filePath: version.file_path,
        source: version.source,
        attachmentList: (version.model_config?.files || []).map((item) => ({
          id:
            item || `attachment-${Math.random().toString(36).substring(2, 15)}`,
          type: "url",
          url: convertFilePathToUrl(item),
        })),
        status: "success",
        model: resolveModel?.(version.model_name),
        dialogue: "",
        voiceId: "",
        shotDuration: shot.duration,
        prompt: version.related_data.start_frame_image_prompt || "",
        createdTime: new Date(version.create_time).getTime(),
      };
      return applyResolvedFields(v, "image", playbackMode);
    });

    // Detect newly generated versions and mark them as unseen
    generatedVersions.forEach((v) => {
      if (unseenVersionStore.isPendingTask(v.id)) {
        unseenVersionStore.addUnseenVersion(v.id);
        unseenVersionStore.removePendingTaskId(v.id);
      }
    });

    // Get generating versions from RTC tasks
    const taskVersions = rtcTasks
      .filter((task) => {
        return (
          task.data.tool_type === "generate_image_for_shot_by_index" &&
          task.data.call_args.shot_index === index + 1 &&
          task.status !== AgentThreadTaskStatus.Success
        );
      })
      .map((task): MediaVersion => {
        const data = task.data as GenerateImageForShotByIndexTask;
        return {
          id: task.id,
          src: "",
          previewSrc: "",
          filePath: "",
          source: "generate",
          prompt: data.init_args.user_prompt,
          status:
            task.status === AgentThreadTaskStatus.Failed
              ? "failed"
              : "generating",
          createdTime: new Date(task.create_time).getTime(),
          attachmentList: (data.init_args.user_specified_images || []).map(
            (item) => ({
              id:
                item ||
                `attachment-${Math.random().toString(36).substring(2, 15)}`,
              type: "url",
              url: convertFilePathToUrl(item),
            })
          ),
          model: resolveModel?.(data.init_args.i2i_model_name),
          shotDuration: shot.duration,
        };
      })
      .map((v) => applyResolvedFields(v, "image", playbackMode));

    const manualVersions = (manuallyAddedVersions[index] || []).map((v) =>
      applyResolvedFields(
        { ...v, shotDuration: v.shotDuration ?? shot.duration },
        "image",
        playbackMode
      )
    );

    const versions = mergeAndSortVersions(
      generatedVersions,
      taskVersions,
      manualVersions
    );

    let current =
      versions.find((version) => version.id === selectedVersions[index]) ||
      generatedVersions.find(
        (version) => version.filePath === shot.start_frame_image_file_path
      );

    if (current) {
      const overrides = metadataOverrides[index];
      if (overrides) {
        current = { ...current, ...overrides };
      }
      current = applyResolvedFields(current, "image", playbackMode);
    }

    if (!current) {
      return {
        mediaType: "image",
        current: {
          id: shot.start_frame_image_file_path || `shot-image-${index}`,
          src: "",
          previewSrc: "",
          filePath: shot.start_frame_image_file_path,
          source: "generate" as const,
          prompt: shot.start_frame_image_prompt,
          attachmentList: [],
          status: "generating" as const,
          createdTime: 0,
          dialogue: "",
          voiceId: "",
          shotDuration: shot.duration,
        },
        versions: [],
      };
    }

    return {
      current,
      versions,
      mediaType: "image",
    };
  });
}

export function buildVideoStoryboardItems(
  data: ArtifactsViewerData["videoStoryboard"]["data"],
  context: BuildContext,
  viewState: MediaViewState = {
    manuallyAddedVersions: {},
    selectedVersions: {},
    metadataOverrides: {},
  }
): MediaEditorItem[] {
  const { rtcTasks, playbackMode, resolveModel } = context;
  const { manuallyAddedVersions, selectedVersions, metadataOverrides } =
    viewState;
  return data.shots.map((shot, index): MediaEditorItem => {
    const metadata = data.shotsMetadatas[index];
    const metadataVersions = metadata?.video_clip.versions || [];
    const generatedVersions = metadataVersions.map((version): MediaVersion => {
      const v: MediaVersion = {
        id: version.identity || version.file_path,
        src: convertFilePathToUrl(version.file_path),
        previewSrc: convertFilePathToUrl(
          version.related_data?.start_frame_image_file_path ||
            version.model_config?.files?.[0] ||
            ""
        ),
        filePath: version.file_path,
        source: version.source,
        attachmentList: (version.model_config?.files || []).map((item) => ({
          id:
            item || `attachment-${Math.random().toString(36).substring(2, 15)}`,
          type: "url",
          url: convertFilePathToUrl(item),
        })),
        status: "success",
        model: resolveModel?.(version.model_name),
        dialogue: version.related_data?.line_content || "",
        voiceId:
          version.related_data?.avatar_audio_type ===
          AvatarAudioType.ExtractMusic
            ? ORIGINAL_MUSIC_VOICE_ID
            : version.model_config?.avatar_voice_id ||
              version.model_config?.avatar_voice_path ||
              "",
        shotDuration: shot.duration,
        prompt: version.related_data?.video_clip_prompt || "",
        createdTime: new Date(version.create_time).getTime(),
        avatarAudioType: shot.avatar_audio_type || AvatarAudioType.None,
        videoClipStartTime: shot.video_clip_start_time ?? undefined,
      };
      return applyResolvedFields(v, "video", playbackMode);
    });

    // Detect newly generated versions and mark them as unseen
    generatedVersions.forEach((v) => {
      if (unseenVersionStore.isPendingTask(v.id)) {
        unseenVersionStore.addUnseenVersion(v.id);
        unseenVersionStore.removePendingTaskId(v.id);
      }
    });

    // Get generating versions from RTC tasks
    const taskVersions = rtcTasks
      .filter((task) => {
        return (
          task.data.tool_type === "generate_video_for_shot_by_index" &&
          task.data.call_args.shot_index === index + 1 &&
          task.status !== AgentThreadTaskStatus.Success
        );
      })
      .map((task): MediaVersion => {
        const data = task.data as GenerateVideoForShotByIndexTask;
        const modelName =
          data.init_args.t2v_model_name ||
          data.init_args.i2v_model_name ||
          data.init_args.is2v_model_name;

        const previewImage = data.init_args.user_specified_images?.[0];

        return {
          id: task.id,
          src: "", // Video not ready yet
          previewSrc: previewImage ? convertFilePathToUrl(previewImage) : "",
          filePath: "",
          source: "generate",
          prompt: data.init_args.user_prompt,
          status:
            task.status === AgentThreadTaskStatus.Failed
              ? "failed"
              : "generating",
          createdTime: new Date(task.create_time).getTime(),
          attachmentList: (data.init_args.user_specified_images || []).map(
            (item) => ({
              id:
                item ||
                `attachment-${Math.random().toString(36).substring(2, 15)}`,
              type: "url",
              url: convertFilePathToUrl(item),
            })
          ),
          model: resolveModel?.(modelName),
          dialogue: data.init_args?.line_content || "",
          voiceId:
            data.init_args?.avatar_audio_type === AvatarAudioType.ExtractMusic
              ? ORIGINAL_MUSIC_VOICE_ID
              : data.init_args?.avatar_voice_id ||
                data.init_args?.avatar_voice_path ||
                "",
          shotDuration: shot.duration,
          avatarAudioType: shot.avatar_audio_type || AvatarAudioType.None,
          videoClipStartTime: shot.video_clip_start_time ?? undefined,
        };
      })
      .map((v) => applyResolvedFields(v, "video", playbackMode));

    const manualVersions = (manuallyAddedVersions[index] || []).map((version) =>
      applyResolvedFields(
        {
          ...version,
          shotDuration: version.shotDuration ?? shot.duration,
          avatarAudioType: shot.avatar_audio_type || AvatarAudioType.None,
          videoClipStartTime:
            version.videoClipStartTime ??
            shot.video_clip_start_time ??
            undefined,
        },
        "video",
        playbackMode
      )
    );

    const versions = mergeAndSortVersions(
      generatedVersions,
      taskVersions,
      manualVersions
    );

    let current =
      versions.find((version) => version.id === selectedVersions[index]) ||
      generatedVersions.find(
        (version) => version.filePath === shot.video_clip_file_path
      );

    if (current) {
      const overrides = metadataOverrides[index];
      if (overrides) {
        current = { ...current, ...overrides };
      }
      current = applyResolvedFields(current, "video", playbackMode);
    }

    if (!current) {
      return {
        mediaType: "video",
        current: {
          id: shot.video_clip_file_path || `shot-video-${index}`,
          src: "",
          previewSrc: "",
          filePath: shot.video_clip_file_path || "",
          source: "generate" as const,
          prompt: shot.video_clip_prompt || "",
          attachmentList: [],
          status: "generating" as const,
          createdTime: 0,
          dialogue: shot.line_content || "",
          voiceId:
            shot.avatar_audio_type === AvatarAudioType.ExtractMusic
              ? ORIGINAL_MUSIC_VOICE_ID
              : shot.avatar_audio_file_path || "",
          shotDuration: shot.duration,
          avatarAudioType: shot.avatar_audio_type || AvatarAudioType.None,
          videoClipStartTime: shot.video_clip_start_time ?? undefined,
        },
        versions: [],
      };
    }

    return {
      current,
      versions,
      mediaType: "video",
      dataId: shot.id,
      metadata,
    };
  });
}
